import { useState } from "react";
import { Pause, Play, Square } from "lucide-react";
import { useTimer } from "@/context/TimerContext";
import { fmtHMS, fmtDur, TIME_CATEGORIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function TimerWidget() {
  const { timer, elapsed, pause, resume, stop } = useTimer();
  const [stopOpen, setStopOpen] = useState(false);
  const [category, setCategory] = useState("autre");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  if (!timer) return null;

  const confirmStop = async () => {
    setSaving(true);
    try {
      const entry = await stop(category, note);
      toast.success(`Temps enregistré : ${fmtDur(entry.duration_sec)} pour ${entry.client_name}`);
      setStopOpen(false);
    } catch { toast.error("Erreur lors de l'enregistrement du temps"); }
    finally { setSaving(false); }
  };

  const btn = "w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors";

  return (
    <>
      <div className="fixed bottom-20 sm:bottom-6 left-4 lg:left-72 z-40 flex items-center gap-3 bg-neutral-900 text-white rounded-full pl-4 pr-2.5 py-2 shadow-lg animate-fade-up"
        data-testid="timer-widget">
        <span className={`w-2 h-2 rounded-full shrink-0 ${timer.paused ? "bg-yellow-400" : "bg-red-500 animate-pulse"}`} />
        <span className="text-sm font-medium truncate max-w-[160px]" data-testid="timer-client-name">{timer.client_name}</span>
        <span className="font-mono text-sm tabular-nums" data-testid="timer-elapsed">{fmtHMS(elapsed)}</span>
        {timer.paused ? (
          <button onClick={resume} data-testid="timer-resume" className={btn} title="Reprendre"><Play className="w-3.5 h-3.5" /></button>
        ) : (
          <button onClick={pause} data-testid="timer-pause" className={btn} title="Pause"><Pause className="w-3.5 h-3.5" /></button>
        )}
        <button onClick={() => { setCategory(timer.category || "autre"); setNote(timer.note || ""); setStopOpen(true); }}
          data-testid="timer-stop-open" className={btn} title="Terminer"><Square className="w-3.5 h-3.5" /></button>
      </div>

      <Dialog open={stopOpen} onOpenChange={setStopOpen}>
        <DialogContent className="max-w-md" data-testid="timer-stop-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Terminer le chronomètre</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 text-center">
              <p className="text-xs text-neutral-400">{timer.client_name}</p>
              <p className="font-mono text-3xl font-bold tabular-nums mt-1" data-testid="timer-stop-duration">{fmtHMS(elapsed)}</p>
            </div>
            <div>
              <Label>Type de travail</Label>
              <select data-testid="timer-stop-category" value={category} onChange={(e) => setCategory(e.target.value)}
                className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white">
                {TIME_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Note <span className="text-neutral-400 font-normal">(optionnel)</span></Label>
              <textarea data-testid="timer-stop-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                placeholder="Ex : création des publications de septembre"
                className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" />
            </div>
            <Button data-testid="timer-stop-submit" onClick={confirmStop} disabled={saving}
              className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
              {saving ? "Enregistrement…" : "Enregistrer le temps"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
